import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { resolveNextOptionIndex, type NavigationAction } from '../helpers/resolve-next-option-index.js';
import { findByPrefix } from '../helpers/find-by-prefix.js';

/** What the controller needs from the element it serves. */
export interface ListboxHost extends ReactiveControllerHost, HTMLElement {
    /** Opt-in: only a host with this set gets listbox behaviour at all. */
    listbox: boolean;
    /** Closes the popover reporting `dismiss`, like native light-dismiss would. */
    dismissFromKeyboard(): void;
}

/** How long a typed prefix keeps collecting before it starts over. */
const TYPEAHEAD_RESET_MS = 500;

/**
 * Keyboard navigation for the WAI-ARIA listbox pattern — arrow keys, Home/End,
 * Enter/Space, Escape and typeahead.
 *
 * **Two exceptions leave keys alone.** During an IME composition the keys belong to the
 * composition. In the host's own filter, keys that edit text (Space, letters, Home/End)
 * stay text, and Enter on the clear button keeps its native click.
 *
 * **Opt-in, and that is the point.** The controller does nothing unless the host has
 * `listbox` set. Six places in open2done use this component today, among them the
 * combobox helpers whose popover closes when focus leaves it; moving focus for all of
 * them would be a behaviour change nobody asked for. Stefan decided on 2026-10-03:
 * focus only where it is requested.
 *
 * **Focus lives on the option, and every option keeps `tabindex="-1"`.** That is a
 * zero-delta to the attribute the element's own doc comment protects: a pointerdown
 * must land focus on the <li>, or `ha-combobox-multi` reads the focus change as
 * "focus left" and closes before the click is through. Roving `tabindex="0"` would
 * have put an option into the Tab order permanently, which matters for the
 * submenu-as-buttons layout that strips the popover attribute.
 */
export class ListboxNavigationController implements ReactiveController {
    #typedPrefix = '';
    #typedAt = 0;

    constructor(private readonly host: ListboxHost) {
        host.addController(this);
    }

    hostConnected(): void {
        this.host.addEventListener('keydown', this.#handleKeydown);
    }

    hostDisconnected(): void {
        this.host.removeEventListener('keydown', this.#handleKeydown);
    }

    /**
     * Moves focus into the list when it opens — the selected option when there is one,
     * otherwise the first selectable one. Called by the host when the popover has opened,
     * except when the host focuses its filter input instead (`focus-filter`).
     */
    focusInitialOption(): void {
        if (!this.host.listbox) {
            return;
        }
        const options = this.#options();
        const selected = options.findIndex(option => option.getAttribute('aria-selected') === 'true');
        const target = selected >= 0
            ? selected
            : resolveNextOptionIndex(this.#navigable(options), -1, 'first');
        options[target]?.focus();
    }

    /** The rendered options, in document order. Dividers are not among them. */
    #options(): HTMLElement[] {
        return [...this.host.querySelectorAll<HTMLElement>('li[role="option"]')];
    }

    #navigable(options: HTMLElement[]): { disabled: boolean; label: string }[] {
        return options.map(option => ({
            disabled: option.getAttribute('aria-disabled') === 'true',
            label: option.textContent?.trim() ?? '',
        }));
    }

    #activeIndex(options: HTMLElement[]): number {
        // The strip's OWN root, not the document: inside a consumer's shadow root
        // `document.activeElement` names that consumer's host element and never an
        // option here (the same trap that broke the arrow keys in @o2d/tab-list 0.1.1).
        const root = this.host.getRootNode() as Document | ShadowRoot;
        return options.indexOf(root.activeElement as HTMLElement);
    }

    #move(action: NavigationAction): void {
        const options = this.#options();
        const next = resolveNextOptionIndex(this.#navigable(options), this.#activeIndex(options), action);
        options[next]?.focus();
    }

    #typeahead(character: string): void {
        const now = Date.now();
        this.#typedPrefix = now - this.#typedAt > TYPEAHEAD_RESET_MS ? character : this.#typedPrefix + character;
        this.#typedAt = now;

        const options = this.#options();
        // A repeated single letter cycles through the matches, so the search starts
        // after the active option; a growing prefix re-searches from before it.
        const from = this.#typedPrefix.length === 1 ? this.#activeIndex(options) : this.#activeIndex(options) - 1;
        const match = findByPrefix(this.#navigable(options), this.#typedPrefix, from);
        if (match !== null) {
            options[match]?.focus();
        }
    }

    /**
     * True for a key this host's own filter keeps for itself: a text-editing key anywhere
     * in the filter (on the clear button, Space keeps its native click), or Enter on the
     * clear button (its native click, which the list's Enter branch would otherwise
     * prevent).
     */
    #isKeyForOwnFilter(event: KeyboardEvent): boolean {
        const filter = this.host.querySelector(':scope > .filter-container');
        if (!filter || !(event.target instanceof Node) || !filter.contains(event.target)) {
            return false;
        }
        if (event.key === 'Enter') {
            return event.target instanceof HTMLButtonElement;
        }
        const printable = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
        return printable || event.key === 'Home' || event.key === 'End';
    }

    #handleKeydown = (event: KeyboardEvent): void => {
        if (!this.host.listbox) {
            return;
        }
        // An IME composition (Japanese, Chinese, …) owns the keys while it runs: arrows pick
        // a candidate and Escape cancels the composition — neither may move or close the list.
        if (event.isComposing || event.keyCode === 229) {
            return;
        }
        // In this list's own filter input, keys that edit text stay text: Space types a
        // space (Ctrl/Meta/Alt combinations stay shortcuts), Home/End move the caret, a
        // letter is typed instead of jumping via typeahead. Arrows (into the list) and
        // Escape keep working from the filter as before. Enter in the input needs no
        // exception: the Enter branch below prevents the default (no form submit) and
        // clicks the active option — none while the caret is in the filter. Enter on the
        // clear button is let through, so its native click still empties the filter.
        // Scoped to the host's own filter, so an ancestor that happens to use the class
        // cannot silence the list.
        if (this.#isKeyForOwnFilter(event)) {
            return;
        }

        const moves: Record<string, NavigationAction> = {
            ArrowDown: 'next',
            ArrowUp: 'previous',
            Home: 'first',
            End: 'last',
        };

        const action = moves[event.key];
        if (action) {
            event.preventDefault(); // or the list scrolls under the moving focus
            this.#move(action);
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const options = this.#options();
            // A real click, so the existing path runs unchanged: `_handleItemClick`
            // fires `item-clicked`, which is what both selection controllers listen to.
            options[this.#activeIndex(options)]?.click();
            return;
        }

        if (event.key === 'Escape') {
            // No preventDefault and no stopPropagation: consumers listen for Escape on
            // their own wrappers and re-check `:popover-open`, so letting it travel is
            // what keeps them working.
            this.host.dismissFromKeyboard();
            return;
        }

        // A single printable character starts or extends the typeahead. Modifier
        // combinations are left alone so shortcuts keep working.
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            this.#typeahead(event.key);
        }
    };
}
